import { produce } from 'immer';
import { nanoid } from 'nanoid';

import { diagramActionTypes } from './diagram.types';

// eslint-disable-next-line
import { getElementsChangesView } from '../../utils/get-tables-changes-view';


const INITIAL_SCALE_VALUE = 0.95; // 1;
const TABLE_ROW_HEIGHT = 36;

let tables_limit_free = 40;
let columns_limit_free = 20;
let notes_limit_free = 20;
let pens_limit_free = 100;
let areas_limit_free = 10;

let tables_limit_max = 400;
let columns_limit_max = 80;
let notes_limit_max = 200;
let pens_limit_max = 400;
let areas_limit_max = 200;


function checkTables(elementsValue, addCount = 1, isP = false) {
    let tablesLimit = isP ? tables_limit_max : tables_limit_free;
    let tablesCount = 0;

    for (const key in elementsValue) {
        const element = elementsValue[key];

        switch (element.type) {
            case 'table': {
                tablesCount++;

                if ((tablesCount + addCount) > tablesLimit) {
                    return false;
                }
                break;
            }
            default:
                break;
        }
    }
    return true;
}

function checkColumns(elementsValue, elementId, addCount = 1, isP = false) {
    let columnsLimit = isP ? columns_limit_max : columns_limit_free;
    if (elementId) {
        const element = elementsValue[elementId];
        const columns = element?.value?.columns;

        if (columns && (Object.keys(columns)?.length + addCount) > columnsLimit) {
            return false;
        }
    }
    return true;
}

function checkNotes(elementsValue, addCount = 1, isP = false) {
    let notesLimit = isP ? notes_limit_max : notes_limit_free;
    let notesCount = 0;

    for (const key in elementsValue) {
        const element = elementsValue[key];

        switch (element.type) {
            case 'note': {
                notesCount++;

                if ((notesCount + addCount) > notesLimit) {
                    return false;
                }
                break;
            }
            default:
                break;
        }
    }
    return true;
}

function checkPens(elementsValue, addCount = 1, isP = false) {
    let pensLimit = isP ? pens_limit_max : pens_limit_free;
    let pensCount = 0;

    for (const key in elementsValue) {
        const element = elementsValue[key];

        switch (element.type) {
            case 'pen-segment': {
                pensCount++;

                if ((pensCount + addCount) > pensLimit) {
                    return false;
                }
                break;
            }
            default:
                break;
        }
    }
    return true;
}

function checkAreas(areasValue, addCount = 1, isP = false) {
    let areasLimit = isP ? areas_limit_max : areas_limit_free;
    if (areasValue && (Object.keys(areasValue)?.length + addCount) > areasLimit) {
        return false;
    }
    return true;
}


const initialState = {
    is_u_p: false,

    // Timestamp string when the open diagram is archived (null when active). Drives
    // the read-only mode flag.
    archived_at: null,

    // pricing plan limit
    isCanvasModalPricingPlanLimitEnabled: false,
    canvasModalPricingPlanLimitType: '',

    // pricing plan limit max
    isCanvasModalPricingPlanLimitMaxEnabled: false,
    canvasModalPricingPlanLimitMaxType: 'tables',

    // BASIC
    scale: INITIAL_SCALE_VALUE,
    originXY: { x: 0, y: 0 },

    // MODES
    viewMode: 'full', // full|keys|names

    // ITEMS
    elementsLive: {}, // live element positions for other users on the board
    canvasLimits: {},

    // FOCUSED ITEMS
    focusedInsideElement: null,

    // SEARCH SPOTLIGHT | column to paint with the hover background on canvas
    searchHoveredColumn: null,

    // UNIFIED SELECTION
    focusedIds: [],

    // CONTROLS TOP LEFT
    isBtnSettingsEnabled: false,
    isBtnModalShortcutsEnabled: false,
    isBtnNewDiagramEnabled: false,

    isBtnLeftPanelEnabled: false,
    isBtnSearchEnabled: false,

    isBtnExportEnabled: false,

    isBtnExportImageEnabled: false,
    isBtnExportImageClickEnabled: false,

    isBtnExportCodeEnabled: false,

    isBtnSqlSyntaxEnabled: false,
    // sqlSyntax: null,

    isBtnViewModesEnabled: false,
    isBtnRedGreenViewEnabled: false,

    // CONTROLS TOP RIGHT
    isBtnPopupNewsEnabled: false,


    isBtnProfileEnabled: false,


    // CONTROLS LEFT
    isCursorHand: false,
    isBtnNewTableClickEnabled: false,
    isBtnNewTableGrabEnabled: false,
    isBtnNewNoteClickEnabled: false,
    isBtnNewNoteGrabEnabled: false,
    isBtnNewLineEnabled: false,
    isBtnNewAreaEnabled: false,
    isBtnPenEnabled: false,

    // CONTROLS BOTTOM
    isBtnMinimapEnabled: false,
    isBtnInfoResourcesEnabled: false,

    // CONTROLS | FOCUS MODE
    isControlsFocusMode: false,
    isTopControlsHidden: false,
    isLeftControlsHidden: false,

    isBtnGridStyleEnabled: false,

    // PEN MENU
    newPen: {
        color: 'yellow',
        width: 3,
        id: nanoid(),
    },

    // CONTEXT MENU
    isContextMenuShowed: true,
    isContextMenuVisible: true,

    isHistoryClicked: false,
    canUndo: false,
    canRedo: false,

    isEnabledElementDescriptionEditing: false,

    // modal diagram export sql
    isModalDiagramExportSqlEnabled: false,
    modalDiagramExportSqlData: { dbEngine: null },

};


function unfocusFocusedItems(state, draft) {
    if (state.focusedInsideElement) {
        draft.focusedInsideElement = null;
    }
    if (state.focusedIds?.length) {
        draft.focusedIds = [];
    }
    return draft;
}



function unfocusEnabledControls(state, draft, exceptControl = null, exceptControlSecond = null, resetControlsFocusMode = false) {
    // CONTROLS TOP LEFT
    if (state.isBtnSettingsEnabled && exceptControl !== 'isBtnSettingsEnabled') {
        draft.isBtnSettingsEnabled = false;
    }
    if (state.isBtnNewDiagramEnabled && exceptControl !== 'isBtnNewDiagramEnabled') {
        draft.isBtnNewDiagramEnabled = false;
    }
    if (state.isBtnModalShortcutsEnabled && exceptControl !== 'isBtnModalShortcutsEnabled') {
        draft.isBtnModalShortcutsEnabled = false;
    }

    if (state.isBtnProfileEnabled && exceptControl !== 'isBtnProfileEnabled') {
        draft.isBtnProfileEnabled = false;
    }

    if (state.isBtnSearchEnabled && exceptControl !== 'isBtnSearchEnabled') {
        draft.isBtnSearchEnabled = false;
        // the search-driven column highlight must never outlive the search
        draft.searchHoveredColumn = null;
    }

    if (state.isBtnExportEnabled && exceptControl !== 'isBtnExportEnabled') {
        draft.isBtnExportEnabled = false;
    }
    if (state.isBtnExportImageEnabled && exceptControl !== 'isBtnExportImageEnabled' && exceptControlSecond !== 'isBtnExportImageEnabled') {
        draft.isBtnExportImageEnabled = false;
    }
    if (state.isBtnExportCodeEnabled && exceptControl !== 'isBtnExportCodeEnabled') {
        draft.isBtnExportCodeEnabled = false;
    }

    if (state.isBtnSqlSyntaxEnabled && exceptControl !== 'isBtnSqlSyntaxEnabled') {
        draft.isBtnSqlSyntaxEnabled = false;
    }


    if (state.isBtnViewModesEnabled && exceptControl !== 'isBtnViewModesEnabled') {
        draft.isBtnViewModesEnabled = false;
    }
    if (state.isBtnGridStyleEnabled && exceptControl !== 'isBtnGridStyleEnabled') {
        draft.isBtnGridStyleEnabled = false;
    }
    if (state.isBtnRedGreenViewEnabled && exceptControl !== 'isBtnRedGreenViewEnabled') {
        draft.isBtnRedGreenViewEnabled = false;
    }

    // CONTROLS LEFT
    if (state.isBtnNewTableClickEnabled && exceptControl !== 'isBtnNewTableClickEnabled') {
        draft.isBtnNewTableClickEnabled = false;
    }
    if (state.isBtnNewTableGrabEnabled && exceptControl !== 'isBtnNewTableGrabEnabled') {
        draft.isBtnNewTableGrabEnabled = false;
    }
    if (state.isBtnNewNoteClickEnabled && exceptControl !== 'isBtnNewNoteClickEnabled') {
        draft.isBtnNewNoteClickEnabled = false;
    }
    if (state.isBtnNewNoteGrabEnabled && exceptControl !== 'isBtnNewNoteGrabEnabled') {
        draft.isBtnNewNoteGrabEnabled = false;
    }
    if (state.isBtnNewLineEnabled && exceptControl !== 'isBtnNewLineEnabled') {
        draft.isBtnNewLineEnabled = false;
    }
    if (state.isBtnNewAreaEnabled && exceptControl !== 'isBtnNewAreaEnabled') {
        draft.isBtnNewAreaEnabled = false;
    }
    if (state.isBtnPenEnabled && exceptControl !== 'isBtnPenEnabled') {
        draft.isBtnPenEnabled = false;
    }

    if (state.isBtnInfoResourcesEnabled && exceptControl !== 'isBtnInfoResourcesEnabled') {
        draft.isBtnInfoResourcesEnabled = false;
    }

    // if (state.isCursorHand && exceptControl !== 'isCursorHand') {
    //     draft.isCursorHand = false;
    // }

    if (draft.isHistoryClicked) {
        draft.isHistoryClicked = false;
    }

    // CONTROLS FOCUS MODE (fullscreen) — reset only when the caller asks for it
    // (Escape). Left off for the normal "unfocus other controls" calls so using
    // a tool while focus mode is on does not kick you out of it.
    if (resetControlsFocusMode && state.isControlsFocusMode) {
        draft.isControlsFocusMode = false;
        draft.isTopControlsHidden = false;
        draft.isLeftControlsHidden = false;
    }

    return draft;
}




const diagramReducer = (state = initialState, action) => {
    if (state.isStoredState) {
        state = { ...initialState, ...state, isStoredState: false };
    }

    return produce(state, (draft) => {
        switch (action.type) {
            case diagramActionTypes.CHANGE_CURSOR: {
                draft.isCursorHand = !state.isCursorHand;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                break;
            }

            // ------------------------------------------------------------------

            case diagramActionTypes.UPDATE_FOCUSED_IDS: {
                draft.focusedInsideElement = null;
                draft.focusedIds = action.payload;
                break;
            }

            case diagramActionTypes.UPDATE_LIVE_ELEMENTS_POSITIONS: {
                for (const key in action.payload) {
                    const oldElement = state.elementsLive[key];
                    const data = action.payload[key];

                    // 1. update elements live
                    draft.elementsLive[key] = {
                        ...oldElement,
                        x: data.x || 0,
                        y: data.y || 0,
                        width: data.width || oldElement.width || 306,
                        height: data.height || oldElement.height || 200,
                    };
                }
                break;
            }

            // 1. CONTROLS LEFT | NEW TABLE CLICK
            case diagramActionTypes.ENABLE_BTN_NEW_TABLE_CLICK: {
                draft.isBtnNewTableClickEnabled =
                    !state.isBtnNewTableClickEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnNewTableClickEnabled'
                );

                break;
            }
            case diagramActionTypes.DISABLE_BTN_NEW_TABLE_CLICK: {
                draft.isBtnNewTableClickEnabled = false;
                draft.isCursorHand = false;
                break;
            }

            // 2. CONTROLS LEFT | NEW TABLE GRAB
            case diagramActionTypes.ENABLE_BTN_NEW_TABLE_GRAB: {
                draft.isBtnNewTableGrabEnabled = true;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnNewTableGrabEnabled'
                );
                break;
            }
            case diagramActionTypes.DISABLE_BTN_NEW_TABLE_GRAB: {
                draft.isBtnNewTableGrabEnabled = false;
                draft.isCursorHand = false;

                break;
            }

            // 3. CONTROLS LEFT | NEW NOTE CLICK
            case diagramActionTypes.ENABLE_BTN_NEW_NOTE_CLICK: {
                draft.isBtnNewNoteClickEnabled =
                    !state.isBtnNewNoteClickEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnNewNoteClickEnabled'
                );

                break;
            }
            case diagramActionTypes.DISABLE_BTN_NEW_NOTE_CLICK: {
                draft.isBtnNewNoteClickEnabled = false;
                draft.isCursorHand = false;

                break;
            }

            // 4. CONTROLS LEFT | NEW NOTE GRAB
            case diagramActionTypes.ENABLE_BTN_NEW_NOTE_GRAB: {
                draft.isBtnNewNoteGrabEnabled = true;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnNewNoteGrabEnabled'
                );

                break;
            }
            case diagramActionTypes.DISABLE_BTN_NEW_NOTE_GRAB: {
                draft.isBtnNewNoteGrabEnabled = false;
                draft.isCursorHand = false;

                break;
            }

            // 5. CONTROLS LEFT | NEW LINE
            case diagramActionTypes.TOGGLE_BTN_NEW_LINE: {
                draft.isBtnNewLineEnabled = !state.isBtnNewLineEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnNewLineEnabled'
                );

                break;
            }

            case diagramActionTypes.DISABLE_BTN_NEW_AREA: {
                draft.isBtnNewAreaEnabled = false;
                draft.isCursorHand = false;
                break;
            }

            case diagramActionTypes.TOGGLE_BTN_NEW_AREA: {
                if (action.payload.isKeyClick) {
                    // Data mutation (area creation, element areaId updates, focus changes)
                    // is handled by the onToggleBtnNewArea saga.
                    // The reducer does nothing here so the saga can run without conflict.
                } else {
                    draft.isBtnNewAreaEnabled = !state.isBtnNewAreaEnabled;

                    // UNFOCUS FOCUSED ITEMS
                    draft = unfocusFocusedItems(state, draft);

                    // UNFOCUS ENABLED CONTROLS
                    draft = unfocusEnabledControls(
                        state,
                        draft,
                        'isBtnNewAreaEnabled'
                    );
                }

                break;
            }

            case diagramActionTypes.TOGGLE_BTN_PEN: {
                draft.isBtnPenEnabled = !state.isBtnPenEnabled;

                // update newPen.id
                if (draft.isBtnPenEnabled) {
                    draft.newPen.id = nanoid();
                    draft.newPen.color = 'yellow';
                }

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isBtnPenEnabled');
                break;
            }

            case diagramActionTypes.SET_ELEMENTS_VIEW_MODE: {
                draft.viewMode = action.payload.viewMode;
                draft.isBtnViewModesEnabled = false;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnViewModesEnabled',
                    'isBtnExportImageEnabled'
                );
                // Keep the settings menu open: picking a view mode from the
                // settings menu's flyout must not close it (helper has only
                // two exception slots, both taken — restore the flag instead;
                // the standalone view-modes dropdown can't be open at the
                // same time as settings, so it is unaffected).
                draft.isBtnSettingsEnabled = state.isBtnSettingsEnabled;

                break;
            }

            // 7. CONTROLS TOP | EXPORT
            case diagramActionTypes.TOGGLE_BTN_EXPORT: {
                draft.isBtnExportEnabled = !state.isBtnExportEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnExportEnabled'
                );

                break;
            }
            // 7. CONTROLS TOP | EXPORT
            case diagramActionTypes.DISABLE_BTN_EXPORT: {
                draft.isBtnExportEnabled = false;
                break;
            }

            // 7. CONTROLS TOP | EXPORT IMAGE
            case diagramActionTypes.TOGGLE_BTN_EXPORT_IMAGE: {
                draft.isBtnExportImageEnabled = !state.isBtnExportImageEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnExportImageEnabled'
                );
                break;
            }
            // 7. CONTROLS TOP | EXPORT IMAGE
            case diagramActionTypes.DISABLE_BTN_EXPORT_IMAGE: {
                draft.isBtnExportImageEnabled = false;
                draft.isBtnExportEnabled = false;
                break;
            }

            // 7. CONTROLS TOP | EXPORT CODE
            case diagramActionTypes.TOGGLE_BTN_EXPORT_CODE: {
                draft.isBtnExportCodeEnabled = !state.isBtnExportCodeEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnExportCodeEnabled'
                );

                // if (draft.isBtnExportCodeEnabled) {
                draft.isBtnExportEnabled = true; // the same as 'isBtnExportCodeEnabled'
                // }

                break;
            }
            // 7. CONTROLS TOP | EXPORT CODE
            case diagramActionTypes.DISABLE_BTN_EXPORT_CODE: {
                draft.isBtnExportCodeEnabled = false;
                draft.isBtnExportEnabled = false;
                break;
            }

            // 7. CONTROLS TOP | VIEW MODES
            case diagramActionTypes.TOGGLE_BTN_VIEW_MODES: {
                draft.isBtnViewModesEnabled = !state.isBtnViewModesEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnViewModesEnabled',
                    'isBtnExportImageEnabled'
                );

                break;
            }

            case diagramActionTypes.TOGGLE_BTN_SQL_SYNTAX: {
                draft.isBtnSqlSyntaxEnabled = !state.isBtnSqlSyntaxEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isBtnSqlSyntaxEnabled');
                break;
            }

            case diagramActionTypes.SET_SQL_SYNTAX: {
                // draft.sqlSyntax = action.payload.sqlSyntax;
                draft.isBtnSqlSyntaxEnabled = false;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isBtnSqlSyntaxEnabled');

                break;
            }


            // 7. CONTROLS TOP | VIEW MODES
            case diagramActionTypes.DISABLE_BTN_VIEW_MODES: {
                draft.isBtnViewModesEnabled = false;
                break;
            }

            // 7. CONTROLS TOP | SEARCH
            case diagramActionTypes.TOGGLE_BTN_SEARCH: {
                draft.isBtnSearchEnabled = !state.isBtnSearchEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnSearchEnabled'
                );

                draft.searchHoveredColumn = null;

                break;
            }

            case diagramActionTypes.DISABLE_BTN_SEARCH: {
                if (state.isBtnSearchEnabled) {
                    draft.isBtnSearchEnabled = false;
                    // UNFOCUS FOCUSED ITEMS (hover-focus must not outlive the search)
                    draft = unfocusFocusedItems(state, draft);
                }
                draft.searchHoveredColumn = null;
                break;
            }

            case diagramActionTypes.SET_SEARCH_HOVERED_COLUMN: {
                draft.searchHoveredColumn = action.payload ?? null;
                break;
            }

            case diagramActionTypes.TOGGLE_BTN_SETTINGS: {
                draft.isBtnSettingsEnabled = !state.isBtnSettingsEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isBtnSettingsEnabled');
                break;
            }

            case diagramActionTypes.TOGGLE_BTN_NEW_DIAGRAM: {
                draft.isBtnNewDiagramEnabled = !state.isBtnNewDiagramEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isBtnNewDiagramEnabled');
                break;
            }

            case diagramActionTypes.TOGGLE_BTN_MODAL_SHORTCUTS: {
                draft.isBtnModalShortcutsEnabled = !state.isBtnModalShortcutsEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isBtnModalShortcutsEnabled');
                break;
            }


            case diagramActionTypes.TOGGLE_BTN_PROFILE: {
                draft.isBtnProfileEnabled = !state.isBtnProfileEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isBtnProfileEnabled');
                break;
            }





            // COPY OF CLICK_ESCAPE (IN CANVAS) || MOVE_TO_MAIN || UNFOCUS ENABLED CONTROLS
            case diagramActionTypes.CANVAS_STARTED: {
                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                if (draft.isHistoryClicked) {
                    draft.isHistoryClicked = false;
                }

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(state, draft, 'isCursorHand');

                break;
            }

            // 8. CONTROLS TOP | LEFT PANEL
            case diagramActionTypes.TOGGLE_BTN_LEFT_PANEL: {
                draft.isBtnLeftPanelEnabled = !state.isBtnLeftPanelEnabled;
                break;
            }

            // 9. CONTROLS POPUP | NEWS
            case diagramActionTypes.DISABLE_BTN_POPUP_NEWS: {
                draft.isBtnPopupNewsEnabled = false;

                break;
            }

            // 9. CONTROLS BOTTOM | MINIMAP
            case diagramActionTypes.TOGGLE_BTN_MINIMAP: {
                draft.isBtnMinimapEnabled = !state.isBtnMinimapEnabled;

                break;
            }
            // CONTROLS | FOCUS MODE (fullscreen button enters/exits)
            case diagramActionTypes.TOGGLE_CONTROLS_FOCUS_MODE: {
                const nextFocusMode = !state.isControlsFocusMode;
                draft.isControlsFocusMode = nextFocusMode;
                // Enter -> hide both edges (arrows shown). Exit -> restore both.
                draft.isTopControlsHidden = nextFocusMode;
                draft.isLeftControlsHidden = nextFocusMode;
                break;
            }
            // CONTROLS | FOCUS MODE | TOP ARROW
            case diagramActionTypes.TOGGLE_TOP_CONTROLS: {
                draft.isTopControlsHidden = !state.isTopControlsHidden;
                break;
            }
            // CONTROLS | FOCUS MODE | LEFT ARROW
            case diagramActionTypes.TOGGLE_LEFT_CONTROLS: {
                draft.isLeftControlsHidden = !state.isLeftControlsHidden;
                break;
            }
            // 9. CONTROLS BOTTOM | INFORMATION & RESOURCES
            case diagramActionTypes.TOGGLE_BTN_INFO_RESOURCES: {
                draft.isBtnInfoResourcesEnabled =
                    !state.isBtnInfoResourcesEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnInfoResourcesEnabled'
                );

                break;
            }

            case diagramActionTypes.UPDATE_SCALE_MINUS: {
                const newScale = state.scale / 1.25;
                const clampedScale = Math.min(Math.max(newScale, 0.045), 4.05);

                if (clampedScale == newScale) {
                    const centerX = window.innerWidth / 2;
                    const centerY = window.innerHeight / 2;

                    const zoomFactor = newScale / state.scale;

                    const newOriginX =
                        (state.originXY.x - centerX) * zoomFactor + centerX;
                    const newOriginY =
                        (state.originXY.y - centerY) * zoomFactor + centerY;

                    // 1. update scale
                    draft.scale = newScale;

                    // 2. update originXY
                    draft.originXY.x = newOriginX;
                    draft.originXY.y = newOriginY;
                }
                break;
            }
            case diagramActionTypes.UPDATE_SCALE_PLUS: {
                const newScale = state.scale * 1.25;
                const clampedScale = Math.min(Math.max(newScale, 0.045), 4.05);

                if (clampedScale == newScale) {
                    const centerX = window.innerWidth / 2;
                    const centerY = window.innerHeight / 2;

                    const zoomFactor = newScale / state.scale;

                    const newOriginX =
                        (state.originXY.x - centerX) * zoomFactor + centerX;
                    const newOriginY =
                        (state.originXY.y - centerY) * zoomFactor + centerY;

                    // 1. udpate scale
                    draft.scale = Math.min(Math.max(newScale, 0.045), 4.05);

                    // 2. update originXY
                    draft.originXY.x = newOriginX;
                    draft.originXY.y = newOriginY;
                }
                break;
            }
            case diagramActionTypes.RESET_SCALE: {
                const newScale = INITIAL_SCALE_VALUE;
                const centerX = window.innerWidth / 2;
                const centerY = window.innerHeight / 2;

                const zoomFactor = newScale / state.scale;

                const newOriginX = (state.originXY.x - centerX) * zoomFactor + centerX;
                const newOriginY = (state.originXY.y - centerY) * zoomFactor + centerY;

                // 1. update scale
                draft.scale = newScale;

                // 2. update originXY
                draft.originXY.x = newOriginX;
                draft.originXY.y = newOriginY;
                break;
            }
            case diagramActionTypes.UPDATE_SCALE: {
                // 1. update scale
                draft.scale = Math.min(
                    Math.max(action.payload.scale, 0.045),
                    4.05
                );

                // 2. update originXY
                draft.originXY.x = action.payload.x;
                draft.originXY.y = action.payload.y;
                break;
            }
            case diagramActionTypes.UPDATE_ORIGIN_XY: {
                // 1. update originXY
                draft.originXY.x = action.payload.x;
                draft.originXY.y = action.payload.y;

                // TODO: ?? should be here context menu location? also in scale reducer
                break;
            }

            // UPDATE_SCALE_TO_FIT — handled by onUpdateScaleToFit saga;
            // it reads Yjs and dispatches APPLY_SCALE_TO_FIT with computed values.

            case diagramActionTypes.APPLY_SCALE_TO_FIT: {
                draft.scale = action.payload.scale;
                draft.originXY.x = action.payload.x;
                draft.originXY.y = action.payload.y;
                draft = unfocusFocusedItems(state, draft);
                break;
            }

            case diagramActionTypes.APPLY_VIEWPORT: {
                const clamped = Math.min(Math.max(action.payload.scale, 0.045), 4.05);
                draft.scale = clamped;
                draft.originXY.x = action.payload.x;
                draft.originXY.y = action.payload.y;
                break;
            }






            // FOCUS_ON_FIRST_COLUMN — handled by onFocusOnFirstColumn saga

            case diagramActionTypes.SHOW_CONTEXT_MENU: {
                draft.isContextMenuShowed = true;
                break;
            }
            case diagramActionTypes.HIDE_CONTEXT_MENU: {
                draft.isContextMenuShowed = false;
                break;
            }
            case diagramActionTypes.TOGGLE_VISIBILITY_CONTEXT_MENU: {
                draft.isContextMenuVisible = !state.isContextMenuVisible;
                break;
            }

            // FOCUS_ON_COLUMN_BELOW — handled by onFocusOnColumnBelow saga

            // FOCUS_ON_COLUMN_ABOVE — handled by onFocusOnColumnAbove saga

            case diagramActionTypes.UPDATE_FOCUSED_INSIDE_ELEMENT: {
                if (!action.payload.tableId) {
                    draft.focusedInsideElement = null;
                } else {
                    if (action.payload.insideElementType === 'header') {
                        draft.focusedInsideElement = {
                            elementId: action.payload.tableId,
                            insideElementId: null,
                            insideElementIndex: null,
                            insideElementType: 'header',
                        };
                    } else if (action.payload.insideElementType === 'note-text') {
                        draft.focusedInsideElement = {
                            elementId: action.payload.tableId,
                            insideElementId: null,
                            insideElementIndex: null,
                            insideElementType: 'note-text',
                        };
                    } else {
                        draft.focusedInsideElement = {
                            elementId: action.payload.tableId,
                            insideElementId: action.payload.columnId,
                            insideElementIndex: action.payload.columnIndex ?? null,
                            insideElementType: 'column',
                        };
                    }
                }

                break;
            }
            case diagramActionTypes.TOGGLE_BTN_RED_GREEN_VIEW: {
                // draft.elementsChanges = getElementsChangesView(state.elementsLastCommited, state.elements);

                const { NdVmqb5y1G32E14VFk0qF, ...rest } =
                    state.elementsLastCommited;
                draft.elementsChanges = getElementsChangesView(
                    {
                        ...rest,
                        xdY00r0wTzNjGF7wnvITy: {
                            id: 'xdY00r0wTzNjGF7wnvITy',
                            type: 'table',
                            x: 200,
                            y: 350,
                            height: 1020,
                            width: 378,
                            value: {
                                header: {
                                    name: 'bo.accounts',
                                },
                                columns: {
                                    JcWXzFnkjlLwjBQ5rBC81: {
                                        id: 'JcWXzFnkjlLwjBQ5rBC81',
                                        name: 'balance',
                                        type: 'datetime',
                                        isNN: true,
                                    },
                                    '8v0UmP_iUtQjSlVrJW6j8': {
                                        id: '8v0UmP_iUtQjSlVrJW6j8',
                                        name: 'descristion',
                                        type: 'varchar(45)',
                                    },
                                    chsN5S23V9TxyOOqNl7hX: {
                                        id: 'chsN5S23V9TxyOOqNl7hX',
                                        name: 'description',
                                        // "name": "ckcalculated_at",
                                        type: 'tdate',
                                    },
                                    AIP2LCU1H8R_VkDXXMj16: {
                                        id: 'AIP2LCU1H8R_VkDXXMj16',
                                        name: 'dncid',
                                        type: 'int',
                                        isNN: true,
                                    },
                                    '2F4rOWeSINl_UApIVlCAX': {
                                        id: '2F4rOWeSINl_UApIVlCAX',
                                        name: 'created_at',
                                        type: 'datetime',
                                        index: {
                                            type: 'composite',
                                        },
                                    },
                                    jjN8jR1sjgcNhIjnrjz9t: {
                                        id: 'jjN8jR1sjgcNhIjnrjz9t',
                                        name: 'jxfloating_profit',
                                        type: 'decimal(20,6)',
                                    },
                                    h_pezAsrAt0iGxiBLInqa: {
                                        id: 'h_pezAsrAt0iGxiBLInqa',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    'JKQnmQDExFR3AbJEl-5GB': {
                                        id: 'JKQnmQDExFR3AbJEl-5GB',
                                        name: 'lzgroup_id',
                                        type: 'int',
                                        isNN: true,
                                        index: {
                                            type: 'fullText',
                                        },
                                    },
                                    'HhJD7v-MrQGGZl3dG5uJF': {
                                        id: 'HhJD7v-MrQGGZl3dG5uJF',
                                        name: 'mid',
                                        isFK: true,
                                        type: 'int',
                                    },
                                    '0EvMcIJof8Oeo2Diwmjpy': {
                                        id: '0EvMcIJof8Oeo2Diwmjpy',
                                        name: 'nleverage',
                                        type: 'decimal(10,2)',
                                    },
                                    '5MO2PckZQ8r-EpzDiBH8R': {
                                        id: '5MO2PckZQ8r-EpzDiBH8R',
                                        name: 'olifetime_profit',
                                        type: 'decimal(20,6)',
                                    },

                                    111: {
                                        id: '111',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    222: {
                                        id: '222',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    333: {
                                        id: '333',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },

                                    o84D75GxwZOhqLt2kKQKn: {
                                        id: 'o84D75GxwZOhqLt2kKQKn',
                                        name: 'pmargin',
                                        type: 'decimal(20,6)',
                                    },
                                    I8wxQYcICNnrpH6k6QGR1: {
                                        id: 'I8wxQYcICNnrpH6k6QGR1',
                                        name: 'qmargin_level',
                                        type: 'decimal(20,6)',
                                    },
                                    AAD1R3wxkU8lvXZoUrgOS: {
                                        id: 'AAD1R3wxkU8lvXZoUrgOS',
                                        name: 'rmessage_timestamp',
                                        type: 'datetime(3)',
                                        index: {
                                            type: 'composite',
                                        },
                                    },
                                    nRLtt8EfHinYnlo38uvrT: {
                                        id: 'nRLtt8EfHinYnlo38uvrT',
                                        name: 'smode',
                                        type: 'enum()',
                                    },
                                    COKdqhdkyV8VQonBm4itk: {
                                        id: 'COKdqhdkyV8VQonBm4itk',
                                        name: 'tmt_login',
                                        type: 'int',
                                    },
                                    'LvDiFosVAgZWBOh-myP93': {
                                        id: 'LvDiFosVAgZWBOh-myP93',
                                        name: 'umt_server_id',
                                        type: 'varchar(255)',
                                    },
                                    XHGTpinzVph9Ysz6kVEDQ: {
                                        id: 'XHGTpinzVph9Ysz6kVEDQ',
                                        name: 'vstatus_id',
                                        type: 'enum()',
                                    },
                                    '7Z03Um13_1dqxOhmNcL0z': {
                                        id: '7Z03Um13_1dqxOhmNcL0z',
                                        name: 'wsystem_balance',
                                        type: 'decimal(20,6)',
                                    },
                                    mOwtdW3gWbfNXmX0keL4M: {
                                        id: 'mOwtdW3gWbfNXmX0keL4M',
                                        name: 'xsystem_equity',
                                        type: 'decimal(20,6)',
                                    },
                                    '9LVBQ0YVvnvR8TsvWXxOT': {
                                        id: '9LVBQ0YVvnvR8TsvWXxOT',
                                        name: 'ysystem_free_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    'dwI9XVMJuZuv-RBZfebLa': {
                                        id: 'dwI9XVMJuZuv-RBZfebLa',
                                        name: 'ztrading_mode',
                                        type: 'enum()',
                                    },
                                    I5CWpDOZW_YA5sSMWeEBh: {
                                        id: 'I5CWpDOZW_YA5sSMWeEBh',
                                        name: 'updated_at',
                                        type: 'datetime',
                                    },
                                },
                            },
                        },
                        111222: {
                            id: '111222',
                            type: 'table',
                            x: -275,
                            y: 350,
                            height: 1020,
                            width: 378,
                            value: {
                                header: {
                                    name: 'bo.test',
                                },
                                columns: {
                                    JcWXzFnkjlLwjBQ5rBC81: {
                                        id: 'JcWXzFnkjlLwjBQ5rBC81',
                                        name: 'sum_balance',
                                        type: 'datetime',
                                        isNN: true,
                                    },
                                    '8v0UmP_iUtQjSlVrJW6j8': {
                                        id: '8v0UmP_iUtQjSlVrJW6j8',
                                        name: 'descristion',
                                        type: 'varchar(45)',
                                    },
                                    chsN5S23V9TxyOOqNl7hX: {
                                        id: 'chsN5S23V9TxyOOqNl7hX',
                                        name: 'description',
                                        // "name": "ckcalculated_at",
                                        type: 'tdate',
                                    },
                                    AIP2LCU1H8R_VkDXXMj16: {
                                        id: 'AIP2LCU1H8R_VkDXXMj16',
                                        name: 'status',
                                        type: 'int',
                                    },
                                    '2F4rOWeSINl_UApIVlCAX': {
                                        id: '2F4rOWeSINl_UApIVlCAX',
                                        name: 'created_at',
                                        type: 'datetime',
                                        index: {
                                            type: 'composite',
                                        },
                                    },
                                    jjN8jR1sjgcNhIjnrjz9t: {
                                        id: 'jjN8jR1sjgcNhIjnrjz9t',
                                        name: 'jxfloating_profit',
                                        type: 'decimal(20,6)',
                                    },
                                    h_pezAsrAt0iGxiBLInqa: {
                                        id: 'h_pezAsrAt0iGxiBLInqa',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    'JKQnmQDExFR3AbJEl-5GB': {
                                        id: 'JKQnmQDExFR3AbJEl-5GB',
                                        name: 'lzgroup_id',
                                        type: 'int',
                                        isNN: true,
                                        index: {
                                            type: 'fullText',
                                        },
                                    },
                                    'HhJD7v-MrQGGZl3dG5uJF': {
                                        id: 'HhJD7v-MrQGGZl3dG5uJF',
                                        name: 'mid',
                                        isPK: true,
                                        type: 'int',
                                    },
                                    '0EvMcIJof8Oeo2Diwmjpy': {
                                        id: '0EvMcIJof8Oeo2Diwmjpy',
                                        name: 'nleverage',
                                        type: 'decimal(10,2)',
                                    },
                                    '5MO2PckZQ8r-EpzDiBH8R': {
                                        id: '5MO2PckZQ8r-EpzDiBH8R',
                                        name: 'olifetime_profit',
                                        type: 'decimal(20,6)',
                                    },

                                    111: {
                                        id: '111',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    222: {
                                        id: '222',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    333: {
                                        id: '333',
                                        name: 'kyfree_margin',
                                        type: 'decimal(20,6)',
                                    },

                                    o84D75GxwZOhqLt2kKQKn: {
                                        id: 'o84D75GxwZOhqLt2kKQKn',
                                        name: 'pmargin',
                                        type: 'decimal(20,6)',
                                    },
                                    I8wxQYcICNnrpH6k6QGR1: {
                                        id: 'I8wxQYcICNnrpH6k6QGR1',
                                        name: 'qmargin_level',
                                        type: 'decimal(20,6)',
                                    },
                                    AAD1R3wxkU8lvXZoUrgOS: {
                                        id: 'AAD1R3wxkU8lvXZoUrgOS',
                                        name: 'rmessage_timestamp',
                                        type: 'datetime(3)',
                                        index: {
                                            type: 'composite',
                                        },
                                    },
                                    nRLtt8EfHinYnlo38uvrT: {
                                        id: 'nRLtt8EfHinYnlo38uvrT',
                                        name: 'smode',
                                        type: 'enum()',
                                    },
                                    COKdqhdkyV8VQonBm4itk: {
                                        id: 'COKdqhdkyV8VQonBm4itk',
                                        name: 'tmt_login',
                                        type: 'int',
                                    },
                                    'LvDiFosVAgZWBOh-myP93': {
                                        id: 'LvDiFosVAgZWBOh-myP93',
                                        name: 'umt_server_id',
                                        type: 'varchar(255)',
                                    },
                                    XHGTpinzVph9Ysz6kVEDQ: {
                                        id: 'XHGTpinzVph9Ysz6kVEDQ',
                                        name: 'vstatus_id',
                                        type: 'enum()',
                                    },
                                    '7Z03Um13_1dqxOhmNcL0z': {
                                        id: '7Z03Um13_1dqxOhmNcL0z',
                                        name: 'wsystem_balance',
                                        type: 'decimal(20,6)',
                                    },
                                    mOwtdW3gWbfNXmX0keL4M: {
                                        id: 'mOwtdW3gWbfNXmX0keL4M',
                                        name: 'xsystem_equity',
                                        type: 'decimal(20,6)',
                                    },
                                    '9LVBQ0YVvnvR8TsvWXxOT': {
                                        id: '9LVBQ0YVvnvR8TsvWXxOT',
                                        name: 'ysystem_free_margin',
                                        type: 'decimal(20,6)',
                                    },
                                    'dwI9XVMJuZuv-RBZfebLa': {
                                        id: 'dwI9XVMJuZuv-RBZfebLa',
                                        name: 'ztrading_mode',
                                        type: 'enum()',
                                    },
                                    I5CWpDOZW_YA5sSMWeEBh: {
                                        id: 'I5CWpDOZW_YA5sSMWeEBh',
                                        name: 'updated_at',
                                        type: 'datetime',
                                    },
                                },
                            },
                        },
                    },
                    state.elements
                );

                // draft.elementsChanges = getElementsChangesView(state.elementsLastCommited, state.elements);

                draft.isBtnRedGreenViewEnabled =
                    !state.isBtnRedGreenViewEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnRedGreenViewEnabled'
                );

                break;
            }

            case diagramActionTypes.CLICK_ESCAPE: {
                // FOCUSED INSIDE ELEMENT or SELECTED IDS
                if (state.focusedInsideElement) {
                    draft.focusedInsideElement = null;
                } else if (state.focusedIds?.length) {
                    draft.focusedIds = [];
                }

                if (draft.isHistoryClicked) {
                    draft.isHistoryClicked = false;
                }
                // UNFOCUS ENABLED CONTROLS (and exit focus mode / reset top+left)
                draft = unfocusEnabledControls(state, draft, 'isCursorHand', null, true);

                draft.searchHoveredColumn = null;

                break;
            }

            case diagramActionTypes.SET_IS_HISTORY_CLICKED: {
                if (draft.isHistoryClicked) {
                    draft.isHistoryClicked = false;
                }
                break;
            }

            case diagramActionTypes.SET_COLOR_NEW_PEN: {
                draft.newPen.color = action.payload.color;
                break;
            }
            case diagramActionTypes.SET_WIDTH_NEW_PEN: {
                draft.newPen.width = action.payload.width;
                break;
            }
            case diagramActionTypes.CLEAR_NEW_PEN: {
                // Element deletion is handled by the onClearNewPen saga.
                // The reducer only clears focus state.
                draft.focusedInsideElement = null;
                draft.focusedIds = [];
                break;
            }


            case diagramActionTypes.ENABLE_ELEMENT_DESCRIPTION_EDITING: {
                if (!draft.isEnabledElementDescriptionEditing) {
                    draft.isEnabledElementDescriptionEditing = true;
                }
                break;
            }
            case diagramActionTypes.DISABLE_ELEMENT_DESCRIPTION_EDITING: {
                if (draft.isEnabledElementDescriptionEditing) {
                    draft.isEnabledElementDescriptionEditing = false;
                }
                break;
            }


            // FETCH DIAGRAM
            case diagramActionTypes.FETCH_DIAGRAM_REQUEST: {
                draft.loading = true;
                // View mode is session-only AND per-visit (user decision):
                // it isn't persisted anywhere, and because this slice lives
                // across SPA navigation (Diagram → Dashboard → Diagram keeps
                // the store), every diagram open resets it here — otherwise a
                // keys-only view left behind reads as "where are my columns?".
                draft.viewMode = 'full';
                break;
            }
            case diagramActionTypes.FETCH_DIAGRAM_SUCCESS: {
                draft.loading = false;
                draft.error = null;
                // Payload is { data }, so the diagram model is at action.payload.data.
                draft.archived_at = action.payload.data?.archived_at ?? null;
                break;
            }
            case diagramActionTypes.FETCH_DIAGRAM_FAILURE: {
                draft.loading = false;
                draft.error = action.payload;
                break;
            }

            // ------------------------------------------------------------------

            // CANVAS PRICING PLAN LIMIT
            case diagramActionTypes.ENABLE_CANVAS_MODAL_PRICING_PLAN_LIMIT: {
                draft.isCanvasModalPricingPlanLimitEnabled = true;
                draft.canvasModalPricingPlanLimitType = action.payload.type;
                break;
            }
            case diagramActionTypes.DISABLE_CANVAS_MODAL_PRICING_PLAN_LIMIT: {
                draft.isCanvasModalPricingPlanLimitEnabled = false;
                draft.canvasModalPricingPlanLimitType = null;
                break;
            }

            // CANVAS PRICING PLAN LIMIT MAX
            case diagramActionTypes.ENABLE_CANVAS_MODAL_PRICING_PLAN_LIMIT_MAX: {
                draft.isCanvasModalPricingPlanLimitMaxEnabled = true;
                draft.canvasModalPricingPlanLimitMaxType = action.payload.type;
                break;
            }
            case diagramActionTypes.DISABLE_CANVAS_MODAL_PRICING_PLAN_LIMIT_MAX: {
                draft.isCanvasModalPricingPlanLimitMaxEnabled = false;
                draft.canvasModalPricingPlanLimitMaxType = null;
                break;
            }

            // ------------------------------------------------------------------
            // MODAL DIAGRAM EXPORT SQL
            case diagramActionTypes.TOGGLE_MODAL_DIAGRAM_EXPORT_SQL: {
                draft.isModalDiagramExportSqlEnabled = !state.isModalDiagramExportSqlEnabled;
                draft.modalDiagramExportSqlData = { dbEngine: action.payload.dbEngine };

                draft.isBtnExportCodeEnabled = false;
                draft.isBtnExportEnabled = false;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // // UNFOCUS ENABLED CONTROLS
                // draft = unfocusEnabledControls(state, draft, 'isBtnExportCodeEnabled');

                break;
            }
            case diagramActionTypes.DISABLE_MODAL_DIAGRAM_EXPORT_SQL: {
                draft.isModalDiagramExportSqlEnabled = false;
                draft.modalDiagramExportSqlData = { dbEngine: null };

                draft.isBtnExportCodeEnabled = false;
                draft.isBtnExportEnabled = false;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // // UNFOCUS ENABLED CONTROLS
                // draft = unfocusEnabledControls(state, draft, 'isBtnExportCodeEnabled');

                break;
            }

            // FOCUS_ALL_ELEMENTS — handled by onFocusAllElements saga

            case diagramActionTypes.ENABLE_BTN_EXPORT_IMAGE_CLICK: {
                draft.isBtnExportImageClickEnabled = true;
                break;
            }
            case diagramActionTypes.DISABLE_BTN_EXPORT_IMAGE_CLICK: {
                draft.isBtnExportImageClickEnabled = false;
                draft.isBtnExportImageEnabled = false;
                draft.isBtnExportEnabled = false;
                break;
            }

            case diagramActionTypes.TOGGLE_BTN_GRID_STYLE: {
                draft.isBtnGridStyleEnabled = !state.isBtnGridStyleEnabled;

                // UNFOCUS FOCUSED ITEMS
                draft = unfocusFocusedItems(state, draft);

                // UNFOCUS ENABLED CONTROLS
                draft = unfocusEnabledControls(
                    state,
                    draft,
                    'isBtnGridStyleEnabled',
                    'isBtnExportImageEnabled'
                );

                break;
            }

            case diagramActionTypes.APPLY_USER_VIEWPORT: {
                const vp = action.payload;
                if (vp == null) break;
                if (vp.scale != null)           draft.scale = vp.scale;
                if (vp.origin_x != null)        draft.originXY.x = vp.origin_x;
                if (vp.origin_y != null)        draft.originXY.y = vp.origin_y;
                if (vp.minimap_open != null)    draft.isBtnMinimapEnabled = vp.minimap_open;
                // Stale stored keys (view_mode / grid_style / scroll_behavior /
                // diagram_theme) are deliberately ignored — grid is
                // preferences-only, and view mode is session-only: every load
                // starts at the initialState 'full' regardless of what an old
                // saved viewport still carries.
                break;
            }

            case diagramActionTypes.CLEAR_DIAGRAM_DETAILS: {
                // Diagram metadata lives in the Y.Doc now (yMeta).
                // Doc lifecycle teardown clears it; nothing to do here yet.
                break;
            }

            case diagramActionTypes.SET_UNDO_STATE: {
                draft.canUndo = !!action.payload.canUndo;
                draft.canRedo = !!action.payload.canRedo;
                break;
            }


            default:
                break;
        }
    });
};

export default diagramReducer;
